import { Country } from '@/helpers/conf';
import { Locale } from '@/helpers/languages';

/**
 * Static provider -> locale geo-availability rules.
 *
 * Source of truth: the "Provider's producers" restriction table. Each source
 * row lists one or more producer codes (the value behind the URL `[provider]`
 * segment, e.g. `booongo`, `pragmaticplay`) that share one ruleset.
 *
 * Authoring rules:
 * - Only providers that are RESTRICTED in at least one locale are listed here.
 *   A provider absent from the map is open in every locale (this also covers
 *   brand-new providers per spec 3.4 — they get hreflang for all locales).
 * - `en` (root) and `en-IE` are ALWAYS open for every provider (spec 3.2), so
 *   they are never part of a `restricted` array.
 * - Only the 7 "conditional" locales below can be restricted. `"Ontario"` and
 *   blank cells in the source table are treated as restricted.
 */

interface ProviderRule {
  codes: string[];
  /** Locales where this provider is NOT open (the "Restricted"/"Ontario" cells). */
  restricted: Locale[];
}

/** The 9 hreflang-eligible locales (the table columns). Excludes unsupported et-EE / fi. */
export const HREFLANG_LOCALES: Locale[] = [
  Locale.ENGLISH_AUSTRALIA,
  Locale.ENGLISH_NEW_ZEALAND,
  Locale.ENGLISH_CANADA,
  Locale.FRENCH_CANADA,
  Locale.GERMAN,
  Locale.FRENCH_SWITZERLAND,
  Locale.NORWEGIAN,
  Locale.ENGLISH_IRELAND,
  Locale.ENGLISH,
];

const PROVIDER_RULES: ProviderRule[] = [
  {
    codes: ['adlunam', '1x2gaming', 'irondogstudio'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.ENGLISH_CANADA,
      Locale.FRENCH_CANADA,
    ],
  },
  { codes: ['3oaks'], restricted: [Locale.ENGLISH_AUSTRALIA] },
  {
    codes: ['amatic', 'wildcatgaming'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.GERMAN,
      Locale.FRENCH_SWITZERLAND,
    ],
  },
  { codes: ['amigo'], restricted: [Locale.ENGLISH_AUSTRALIA] },
  { codes: ['apparat'], restricted: [Locale.GERMAN] },
  { codes: ['avatarux'], restricted: [Locale.ENGLISH_AUSTRALIA] },
  {
    codes: ['booongo'],
    restricted: [
      Locale.ENGLISH_NEW_ZEALAND,
      Locale.ENGLISH_CANADA,
      Locale.FRENCH_CANADA,
      Locale.GERMAN,
      Locale.FRENCH_SWITZERLAND,
      Locale.NORWEGIAN,
    ],
  },
  {
    codes: ['technology'],
    restricted: [Locale.ENGLISH_CANADA, Locale.FRENCH_CANADA],
  },
  {
    codes: ['amusnetlive', 'amusnet'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.ENGLISH_CANADA,
      Locale.FRENCH_CANADA,
      Locale.FRENCH_SWITZERLAND,
    ],
  },
  { codes: ['endorphina'], restricted: [Locale.ENGLISH_AUSTRALIA] },
  {
    // Evolution and the studios served via Evolution share one ruleset.
    codes: ['evolution', 'redtiger', 'netent', 'bigtimegaming'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.ENGLISH_CANADA,
      Locale.FRENCH_CANADA,
      Locale.FRENCH_SWITZERLAND,
    ],
  },
  {
    codes: ['blueprint'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.ENGLISH_CANADA,
      Locale.FRENCH_CANADA,
      Locale.GERMAN,
      Locale.FRENCH_SWITZERLAND,
    ],
  },
  {
    codes: ['ezugi'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.ENGLISH_CANADA,
      Locale.FRENCH_CANADA,
    ],
  },
  {
    codes: ['gameart'],
    restricted: [Locale.ENGLISH_AUSTRALIA, Locale.ENGLISH_NEW_ZEALAND],
  },
  {
    codes: ['gamomat'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.ENGLISH_CANADA,
      Locale.FRENCH_CANADA,
      Locale.GERMAN,
    ],
  },
  {
    codes: ['habanero'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.ENGLISH_CANADA,
      Locale.FRENCH_CANADA,
    ],
  },
  {
    codes: [
      'foxhoundgames',
      'jinxgaming',
      'pineappleplay',
      'trustygaming',
      'kitsunegaming',
      'backseatgaming',
      'goodtimesstudios',
      'bullsharkgames',
      'nownow',
      'hacksaw',
    ],
    restricted: [Locale.ENGLISH_AUSTRALIA, Locale.NORWEGIAN],
  },
  { codes: ['lucky'], restricted: [Locale.ENGLISH_AUSTRALIA] },
  { codes: ['mrslotty'], restricted: [Locale.GERMAN] },
  { codes: ['netgaming'], restricted: [Locale.FRENCH_SWITZERLAND] },
  {
    codes: ['nolimit'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.ENGLISH_CANADA,
      Locale.FRENCH_CANADA,
    ],
  },
  { codes: ['novomatic'], restricted: [Locale.FRENCH_SWITZERLAND] },
  { codes: ['pgsoft'], restricted: [Locale.ENGLISH_AUSTRALIA] },
  {
    codes: ['playngo'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.GERMAN,
      Locale.FRENCH_SWITZERLAND,
    ],
  },
  {
    codes: ['playson'],
    restricted: [Locale.ENGLISH_CANADA, Locale.FRENCH_CANADA, Locale.GERMAN],
  },
  {
    // "Ontario" cells (en-CA / fr-CA) are treated as restricted.
    codes: ['playtech'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.ENGLISH_CANADA,
      Locale.FRENCH_CANADA,
      Locale.GERMAN,
      Locale.FRENCH_SWITZERLAND,
    ],
  },
  {
    codes: ['pragmaticplay', 'pragmaticplaylive'],
    restricted: [Locale.ENGLISH_AUSTRALIA],
  },
  {
    codes: ['quickfire'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.GERMAN,
      Locale.FRENCH_SWITZERLAND,
    ],
  },
  {
    codes: ['quickspin'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.GERMAN,
      Locale.FRENCH_SWITZERLAND,
    ],
  },
  {
    codes: [
      '4theplayer',
      'reelplay',
      'printstudios',
      'boomerangstudios',
      'fourleafgaming',
      'electricelephant',
    ],
    restricted: [Locale.ENGLISH_AUSTRALIA],
  },
  {
    codes: ['madhatgames', 'rubyplay', 'koala'],
    restricted: [Locale.ENGLISH_AUSTRALIA],
  },
  {
    codes: ['smartsoft'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.ENGLISH_CANADA,
      Locale.FRENCH_CANADA,
    ],
  },
  {
    codes: ['reelnoise', 'retrogaming', 'spnmnl'],
    restricted: [Locale.ENGLISH_AUSTRALIA],
  },
  {
    codes: ['swntt'],
    restricted: [Locale.ENGLISH_CANADA, Locale.FRENCH_CANADA],
  },
  {
    codes: ['thunderkick'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.ENGLISH_CANADA,
      Locale.FRENCH_CANADA,
    ],
  },
  {
    codes: ['tornadogames'],
    restricted: [
      Locale.ENGLISH_AUSTRALIA,
      Locale.ENGLISH_NEW_ZEALAND,
      Locale.FRENCH_SWITZERLAND,
    ],
  },
  { codes: ['voltent'], restricted: [Locale.FRENCH_SWITZERLAND] },
];

/** Flattened O(1) lookup: provider code -> its rule. Built once at module init. */
const PROVIDER_ACCESS: ReadonlyMap<string, ProviderRule> = (() => {
  const map = new Map<string, ProviderRule>();
  for (const rule of PROVIDER_RULES) {
    for (const code of rule.codes) {
      map.set(code, rule);
    }
  }
  return map;
})();

/**
 * Render gate: is the game's provider available (launcher) in this locale?
 * Unknown provider or all-open provider -> open everywhere.
 */
export const isProviderOpenForLocale = (
  provider: string,
  locale: string
): boolean => {
  const rule = PROVIDER_ACCESS.get(provider);
  if (!rule) return true;

  return !rule.restricted.includes(locale as Locale);
};

/**
 * Maps a CF-IPCountry code to the locale whose table column governs that region.
 * NB: Switzerland -> fr-CH (the column the table uses), unlike
 * `getLanguageByCountry` which returns `de` for CH.
 */
export const COUNTRY_TO_LOCALE: Partial<Record<Country, Locale>> = {
  [Country.Australia]: Locale.ENGLISH_AUSTRALIA,
  [Country.NewZealand]: Locale.ENGLISH_NEW_ZEALAND,
  [Country.Canada]: Locale.ENGLISH_CANADA,
  [Country.Germany]: Locale.GERMAN,
  [Country.Switzerland]: Locale.FRENCH_SWITZERLAND,
  [Country.Norway]: Locale.NORWEGIAN,
  [Country.Ireland]: Locale.ENGLISH_IRELAND,
};

/**
 * Render gate keyed on the user's region (CF-IPCountry) rather than the URL
 * locale. Unmapped / empty country -> `en` (always open) -> treated as open.
 */
export const isProviderOpenForCountry = (
  provider: string,
  countryCode: string
): boolean =>
  isProviderOpenForLocale(
    provider,
    COUNTRY_TO_LOCALE[countryCode as Country] ?? Locale.ENGLISH
  );

/**
 * hreflang locale set for a provider: only the locales where it is open
 * (plus the always-open `en` / `en-IE`). Unknown provider -> all locales.
 */
export const getOpenLocalesForProvider = (provider: string): Locale[] => {
  const rule = PROVIDER_ACCESS.get(provider);
  if (!rule) return [...HREFLANG_LOCALES];
  return HREFLANG_LOCALES.filter((locale) => !rule.restricted.includes(locale));
};
